import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/audit";
import { useAuth } from "@/hooks/useAuth";

/**
 * CRITICAL SECURITY HOTFIX — Real server-side RBAC.
 *
 * SuperAdmin status is resolved exclusively from Supabase:
 *   1. A valid Supabase auth session must exist.
 *   2. The user must have role `superadmin` or `admin` in `public.user_roles`
 *      (checked via the SECURITY DEFINER RPC `is_superadmin`).
 *
 * The previous `localStorage("ss26.superAdmin")` opt-in flag has been removed —
 * client-side flags can be tampered with from devtools and are not a real
 * authorization boundary. Role assignments now happen only through the DB.
 */
export function useSuperAdmin() {
  const { authReady, session } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let mounted = true;

    if (!authReady) {
      setIsLoading(true);
      return () => { mounted = false; };
    }

    if (!session) {
      setIsSuperAdmin(false);
      setIsLoading(false);
      return () => { mounted = false; };
    }

    const resolve = async () => {
      try {
        const { data, error } = await supabase.rpc("is_superadmin");
        if (!mounted) return;
        if (error) {
          setIsSuperAdmin(false);
        } else {
          const value = Boolean(data);
          setIsSuperAdmin(value);
          if (value) {
            void trackEvent("super_admin_access_verified", { source: "supabase_rbac" });
          }
        }
      } catch {
        if (mounted) setIsSuperAdmin(false);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    void resolve();
    return () => {
      mounted = false;
    };
  }, [authReady, session?.user?.id, session]);

  return { isSuperAdmin, isLoading };
}